import { useTranslation } from 'react-i18next';

import { isPathMatches } from '~/components/app-header/utils/navigation-utils';
import { ChatInputConfig } from '~/components/chat';
import {
  EntityOrThemeWithListTypes,
  isThemeItem,
} from '~/components/search-modal/utils';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useNavigateToThemeOverview } from '~/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import {
  POSTHOG_TRACKING_SOURCES,
  TrackingMetadata,
} from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useChat } from '~/views/alfred/quick-analysis/use-chat/useChat';

interface UseChatInputHandlersProps {
  config?: ChatInputConfig;
  submitCallback?: (message: string) => void;
  isLoading: boolean;
  trackingMetadata?: TrackingMetadata;
}

export const useChatInputHandlers = ({
  config,
  submitCallback,
  isLoading,
  trackingMetadata,
}: UseChatInputHandlersProps) => {
  const { t } = useTranslation('chat');
  const { trackAlfredQuerySent, trackThemeOpen } = usePosthogEvents();

  const { navigateToChatMessage: navigateToAssetOverviewChatMessage } =
    useNavigateToAssetOverview();
  const { navigateToChatMessage: navigateToThemeOverviewChatMessage } =
    useNavigateToThemeOverview();
  const { resetStore, abortRequest, resetSession, sendMessage } = useChat(
    state => ({
      resetStore: state.resetStore,
      abortRequest: state.abortRequest,
      resetSession: state.resetSession,
      sendMessage: state.sendMessage,
    })
  );

  const shouldResetStore = (question: string) => {
    if (!isPathMatches(['alfred'])) {
      return true;
    }

    const restRegex = new RegExp(`^${t('resetSession')}$`, 'i');
    return restRegex.test(question);
  };

  const submitHandler = (
    inputValue: string,
    onSubmitCallbackHandler: () => void
  ) => {
    if (isLoading || !inputValue.trim()) {
      return;
    }

    const question = inputValue.trim();

    if (config?.clearDocumentContextOnSubmit) {
      resetSession(true);
    }

    // reset store if asking question not from alfred
    // in other words, init new session
    if (shouldResetStore(question)) {
      resetStore();
    }

    sendMessage({
      question,
    });

    const messageId = useChat.getState().messages.at(-1)?.id;

    if (trackingMetadata?.source) {
      trackAlfredQuerySent({
        source: trackingMetadata.source,
        type: 'Quick',
        via: trackingMetadata.via,
        value: question,
        messageId,
      });
    } else if (isPathMatches(['alfred'])) {
      trackAlfredQuerySent({
        source: POSTHOG_TRACKING_SOURCES.ALFRED,
        type: 'Quick',
        value: question,
        messageId,
      });
    }

    onSubmitCallbackHandler();
    submitCallback?.(question);
  };

  const onSelect = (item: EntityOrThemeWithListTypes, clear: () => void) => {
    const isTheme = isThemeItem(item);

    if (isTheme) {
      trackThemeOpen(POSTHOG_TRACKING_SOURCES.ALFRED);
      navigateToThemeOverviewChatMessage(item.id);
    } else {
      navigateToAssetOverviewChatMessage(item.tag);
    }

    clear();
  };

  const onKeyDown = (
    _: React.KeyboardEvent<HTMLElement>,
    inputText: string,
    onSubmitCallbackHandler: () => void
  ) => {
    submitHandler(inputText, onSubmitCallbackHandler);
  };

  const onSubmit = (
    event:
      | React.FormEvent<HTMLFormElement>
      | React.KeyboardEvent<HTMLTextAreaElement>,
    inputText: string,
    onSubmitCallbackHandler: () => void
  ) => {
    event.preventDefault();
    submitHandler(inputText, onSubmitCallbackHandler);
  };

  return {
    onSubmit,
    onKeyDown,
    onSelect,
    onAbort: abortRequest,
  };
};
